import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from './authService';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const Itinerary = ({ itinerary, setItinerary }) => {
  const [destinations, setDestinations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredDestinations, setFilteredDestinations] = useState([]);
  const [currentDestination, setCurrentDestination] = useState({
    destinationId: '',
    departureDate: '',
    returnDate: '',
    numberNights: 0,
  });
  const { token } = useAuth();

  useEffect(() => {
    const fetchDestinations = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${API_URL}/api/destinations?size=1000`, { // Fetch all destinations
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setDestinations(data);
        } else {
          console.error('Error fetching destinations');
        }
      } catch (error) {
        console.error('Error fetching destinations:', error);
      }
    };
    fetchDestinations();
  }, [token]);

  useEffect(() => {
    if (currentDestination.departureDate && currentDestination.returnDate) {
      const start = new Date(currentDestination.departureDate);
      const end = new Date(currentDestination.returnDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setCurrentDestination(prev => ({ ...prev, numberNights: diffDays }));
    } else {
      setCurrentDestination(prev => ({ ...prev, numberNights: 0 }));
    }
  }, [currentDestination.departureDate, currentDestination.returnDate]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentDestination(prev => ({ ...prev, destinationId: '' }));
    
    if (value) {
      const filtered = destinations.filter(d => 
        d.country.toLowerCase().includes(value.toLowerCase()) || 
        d.destinationName.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredDestinations(filtered);
    } else {
      setFilteredDestinations(destinations.slice(0, 10));
    }
    setShowSuggestions(true);
  };

  const selectDestination = (dest) => {
    setSearchTerm(`${dest.destinationName} - ${dest.country}`);
    setCurrentDestination(prev => ({ ...prev, destinationId: dest.id }));
    setShowSuggestions(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentDestination(prev => ({ ...prev, [name]: value }));
  };

  const handleAddDestination = () => {
    if (!currentDestination.destinationId) {
      alert('Por favor, seleccione un destino para agregar al itineario.');
      return;
    }
    const destination = destinations.find(d => d.id === parseInt(currentDestination.destinationId));
  
  // Creamos el nombre con el formato deseado
  const fullName = `${destination?.destinationName} - ${destination?.country}`;

  setItinerary([
    ...itinerary, 
    { 
      ...currentDestination, 
      destinationName: fullName, // Guardamos el nombre formateado
      id: Date.now() 
    }
  ]);

  // Reset del formulario...
  setCurrentDestination({
    destinationId: '',
    departureDate: '',
    returnDate: '',
    numberNights: 0,
    });
    setSearchTerm('');
  };

  const handleRemoveDestination = (id) => {
    setItinerary(itinerary.filter(dest => dest.id !== id));
  };

  return (
    <div style={styles.itinerarySection}>
      <h4 style={styles.sectionTitle}>Itinerario de Viaje</h4>
      
      <div style={styles.itineraryForm}>
        <div style={styles.fieldContainer}>
          <label style={styles.label}>Destino</label>
          <div style={styles.autocompleteContainer}>
            <input 
              type="text" 
              placeholder="Buscar destino..." 
              value={searchTerm} 
              onChange={handleSearchChange}
              onFocus={() => {
                if (!searchTerm) setFilteredDestinations(destinations.slice(0, 10));
                setShowSuggestions(true);
              }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              style={styles.input} 
            />
            {showSuggestions && (
              <div style={styles.suggestionsDropdown}>
                {filteredDestinations.map(dest => (
                  <div key={dest.id} style={styles.suggestionItem} onClick={() => selectDestination(dest)}>
                    {dest.destinationName} - {dest.country}
                  </div>
                ))}
                {filteredDestinations.length === 0 && (
                  <div style={styles.suggestionItem}>No se encontraron destinos</div>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={styles.fieldContainer}>
          <label style={styles.label}>Fecha de Salida</label>
          <input name="departureDate" type="date" value={currentDestination.departureDate} onChange={handleInputChange} style={styles.input} />
        </div>
        <div style={styles.fieldContainer}>
          <label style={styles.label}>Fecha de Regreso</label>
          <input name="returnDate" type="date" value={currentDestination.returnDate} onChange={handleInputChange} style={styles.input} />
        </div>
        <div style={styles.fieldContainer}>
          <label style={styles.label}>Noches</label>
          <input type="text" value={currentDestination.numberNights} style={styles.input} readOnly />
        </div>
        <button type="button" onClick={handleAddDestination} style={styles.addButton}>Agregar al Itinerario</button>
      </div>

      {itinerary.length > 0 && (
        <div style={styles.itineraryList}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Destino</th>
                <th style={styles.tableHeader}>Salida</th>
                <th style={styles.tableHeader}>Regreso</th>
                <th style={styles.tableHeader}>Noches</th>
                <th style={styles.tableHeader}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {itinerary.map((item) => (
                <tr key={item.id}>
                  <td style={styles.tableCell}>{item.destinationName}</td>
                  <td style={styles.tableCell}>{formatDate(item.departureDate)}</td>
                  <td style={styles.tableCell}>{formatDate(item.returnDate)}</td>
                  <td style={styles.tableCell}>{item.numberNights}</td>
                  <td style={styles.tableCell}>
                    <button onClick={() => handleRemoveDestination(item.id)} style={styles.deleteButton}>Quitar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
    itinerarySection: { marginBottom: '30px', padding: '20px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #e0e0e0' },
    sectionTitle: { fontSize: '20px', fontWeight: '600', color: '#495057', marginBottom: '15px', borderBottom: '2px solid #ff9f43', paddingBottom: '5px' },
    itineraryForm: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', alignItems: 'flex-end', marginBottom: '20px' },
    fieldContainer: { display: 'flex', flexDirection: 'column' },
    label: { fontSize: '16px', fontWeight: 'bold', color: '#495057', marginBottom: '4px' },
    input: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px' },
    select: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px' },
    addButton: { backgroundColor: '#5cb85c', color: 'white', padding: '12px 20px', border: 'none', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', alignSelf: 'center', justifySelf:'center', height:'45px', marginTop:'20px' },
    itineraryList: { marginTop: '20px' },
    table: { width: '100%', borderCollapse: 'collapse' },
    tableHeader: { backgroundColor: '#f2f2f2', padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' },
    tableCell: { padding: '12px', borderBottom: '1px solid #ddd' },
    deleteButton: { backgroundColor: '#d9534f', color: 'white', padding: '8px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer' },
    autocompleteContainer: { position: 'relative' },
    suggestionsDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: '200px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
    suggestionItem: { padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f1f1', fontSize: '16px' },
};
  
export default Itinerary;
